import { createSlice } from "@reduxjs/toolkit";
import {
  createCertificateType,
  getActiveCertificateTypes,
  getAllCertificateTypes,
  updateCertificateType,
} from "./certificateTypeService";

/* -------------------- Status Enum -------------------- */

export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
};

/* -------------------- Initial State -------------------- */

const initialState = {
  createCertificateType: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },

  certificateTypeList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
    pageCount: 0,
  },

  updateCertificateType: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },

  activeCertificateTypesList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },
};

/* -------------------- Slice -------------------- */

const certificateTypeSlice = createSlice({
  name: "certificateType",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    /* ---------- Create Certificate Type ---------- */
    builder
      .addCase(createCertificateType.pending, (state) => {
        state.createCertificateType.status = STATUS.LOADING;
        state.createCertificateType.error = null;
      })
      .addCase(createCertificateType.fulfilled, (state, action) => {
        state.createCertificateType.status = STATUS.SUCCEEDED;
        state.createCertificateType.message = action?.payload;
      })
      .addCase(createCertificateType.rejected, (state, action) => {
        state.createCertificateType.status = STATUS.FAILED;
        state.createCertificateType.error = action?.payload;
      });

    /* ---------- Get Certificate Type List ---------- */
    builder
      .addCase(getAllCertificateTypes.pending, (state) => {
        state.certificateTypeList.status = STATUS.LOADING;
        state.certificateTypeList.error = null;
      })
      .addCase(getAllCertificateTypes.fulfilled, (state, action) => {
        state.certificateTypeList.status = STATUS.SUCCEEDED;
        state.certificateTypeList.data = action?.payload || [];
        state.certificateTypeList.pageCount = action.payload?.pageCount || 0;
      })
      .addCase(getAllCertificateTypes.rejected, (state, action) => {
        state.certificateTypeList.status = STATUS.FAILED;
        state.certificateTypeList.error = action.payload?.message;
      });

    /* ---------- Update Certificate Type ---------- */
    builder
      .addCase(updateCertificateType.pending, (state) => {
        state.updateCertificateType.status = STATUS.LOADING;
        state.updateCertificateType.error = null;
      })
      .addCase(updateCertificateType.fulfilled, (state, action) => {
        state.updateCertificateType.status = STATUS.SUCCEEDED;
        state.updateCertificateType.message = action?.payload;
      })
      .addCase(updateCertificateType.rejected, (state, action) => {
        state.updateCertificateType.status = STATUS.FAILED;
        state.updateCertificateType.error = action?.payload;
      });

    /* ---------- Get Active Certificate Entity List ---------- */
    builder
      .addCase(getActiveCertificateTypes.pending, (state) => {
        state.activeCertificateTypesList.status = STATUS.LOADING;
        state.activeCertificateTypesList.error = null;
      })
      .addCase(getActiveCertificateTypes.fulfilled, (state, action) => {
        state.activeCertificateTypesList.status = STATUS.SUCCEEDED;
        state.activeCertificateTypesList.data = action?.payload || [];
      })
      .addCase(getActiveCertificateTypes.rejected, (state, action) => {
        state.activeCertificateTypesList.status = STATUS.FAILED;
        state.activeCertificateTypesList.error = action.payload?.message;
      });
  },
});

/* -------------------- Selectors -------------------- */

export const selectCreateCertificate = (state) =>
  state.certificateType.createCertificateType;

export const selectCertificateList = (state) =>
  state.certificateType.certificateTypeList;

export const selectUpdateCertificateType = (state) =>
  state.certificateType.updateCertificateType;

export const selectActiveCertificateTypesList = (state) =>
  state.certificateType.activeCertificateTypesList;

export default certificateTypeSlice.reducer;
